import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    UserLogDTO,
    useSearchUserLogApi,
    useSaveUserLogApi,
    useDeleteUserLogApi,
} from '../../../api/user-log-api';
import SvgAdd from '../../../assets/icons/Add';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { FormikCellToggle } from '../../../components/formik';
import { DateCell, ToggleCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveUserLogModal } from './save-user-log-modal';
import { LoadingButton } from '../../../components/loading-button';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { ModifiedRowInfo } from '../../../components/table/modified-row-info';
import { CreatedRowInfo } from '../../../components/table/created-row-info';

export function UserLogApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedUserLog, setSelectedUserLog] = useState<UserLogDTO | undefined>();
    const userLogInit = useSearchUserLogApi();
    const saveUserLog = useSaveUserLogApi();
    const deleteUserLog = useDeleteUserLogApi();

    const { result, error, busy, initialized, initializing } = userLogInit;

    const userTitle = useCallback(
        (userName?: string) => {
            var user = result?.users.find(x => x.userName === userName);
            return <span>{user ? `${user.firstName} ${user.lastName} ` : userName}</span>;
        },
        [result?.users]
    );

    const columns: IBasicTableColumn<UserLogDTO>[] = useMemo(
        () => [
            {
                Header: <T>serial</T>,
                name: 'serial',
                width: 80,
                Cell: ({ row }) => <N>{row.serial}</N>,
            },
            {
                Header: <T>form</T>,
                name: 'form',
                width: 80,
            },
            {
                Header: <T>action</T>,
                name: 'action',
                width: 80,
            },
            {
                Header: <T>message</T>,
                name: 'message',
                width: 80,
            },
            {
                Header: <T>url</T>,
                name: 'url',
                width: 80,
            },
            {
                Header: <T>session-id</T>,
                name: 'sessionId',
                width: 80,
                Cell: ({ row }) => <N>{row.sessionId}</N>,
            },
            {
                Header: <T>user-name</T>,
                name: 'userName',
                width: 80,
                Cell: ({ row }) => userTitle(row.userName),
            },
            {
                Header: <T>is-history-log</T>,
                name: 'isHistoryLog',
                width: 80,
                Cell: ToggleCell,
            },
        ],
        [userTitle]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        userLogInit.post(null, {});
    }, [initialized, initializing, error, userLogInit]);

    const handleDelete = (userLog: UserLogDTO) => {
        bd.msgbox(
            translate('delete-user-log'),
            translate('confirm-delete-user-log'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteUserLog.remove(
                            userLog,
                            () => {
                                userLogInit.updateResult({
                                    ...result,
                                    userLogs: result.userLogs.filter(
                                        b => b.serial !== userLog.serial
                                    ),
                                });
                                notify.dark(translate('selected-user-log-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedUserLog(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (userLog: UserLogDTO) => {
        saveUserLog.post(
            null,
            userLog,
            res => {
                if (selectedUserLog) {
                    userLogInit.updateResult({
                        ...result,
                        userLogs: result.userLogs.map(b =>
                            b.serial === userLog.serial ? res.userLog : b
                        ),
                    });
                } else {
                    userLogInit.updateResult({
                        ...result,
                        userLogs: [res.userLog, ...result.userLogs],
                    });
                }

                setShowModal(false);
                setSelectedUserLog(undefined);
                notify.success(
                    selectedUserLog
                        ? translate('selected-user-log-updated-successfully')
                        : translate('new-user-log-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => userLogInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="user-log"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-user-log</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedUserLog(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.userLogs}
                        onRowClick={({ row }) => {
                            setSelectedUserLog(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveUserLogModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedUserLog(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedUserLog={selectedUserLog}
                        users={result?.users}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
